import React, { FC, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import Stack from '../stack/Stack';
import Typography from '../typography/Typography';
import { useBackgroundColor } from '../../utils';
import Divider from '../divider/Divider';
import { LinkType } from '../link/Link';
import Input from '../form/input/Input';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';

// Interface

export interface CalculatorProps {
  type: 'overdraft' | 'creditCard';
  defaultInterestRate: number;
  defaultAmount: number;
  defaultNumberOfDays: number;
  maxAmount: number;
  maxInterestRate: number;
  maxNumberOfDays: number;
  stacked?: boolean;
}

interface InputProps {
  label: string;
  value: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  min: number;
  max: number;
  id: string;
  type: 'interest' | 'amount' | 'days';
}

interface StyledContainerProps {
  stacked?: boolean;
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({ theme: { space, colors, media, borderRadii }, stacked }) => css`
    border: solid 1px ${colors.darkBlue};
    border-radius: ${borderRadii.small};
    overflow: hidden;

      ${
        !stacked &&
        css`
          @media (min-width: ${media.medium}) {
            display: grid;
            grid-template-columns: 1fr 1fr;
          }
        `
      }
    }
  `};
`;

const StyledForm = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    ${useBackgroundColor('white')}
    padding: ${space.fluidSmallToLarge};
  `};
`;

const StyledResults = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    ${useBackgroundColor('darkBlue')}
    padding: ${space.fluidSmallToLarge};

    > * {
      max-width: 305px;
    }
  `};
`;

const StyledInputHeader = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    input {
      border: 2px solid ${colors.darkBlue};
      ${useBackgroundColor('white')}
      padding: ${space.xxSmall} ${space.xSmall};
      border-radius: ${borderRadii.small};
      transition: all 0.2s ease-in-out;
      width: 106px;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Works for Firefox */
      &[type='number'] {
        -moz-appearance: textfield;
      }

      // Hover

      &:hover {
        box-shadow: 0 0 0 1px ${colors.darkBlue};
        opacity: 0.9;
      }
    }
  `};
`;

const StyledMinMax = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    justify-content: space-between;
    margin-top: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

const StyledCalcInput = styled.div`
  ${({ theme: { space, colors, borderRadii, media } }) => css``};
`;

const StyledRange = styled.div`
  ${({ theme: { space, colors, media, maxMedia } }) => css`
    position: relative;

    @media (max-width: ${maxMedia.medium}) {
      span {
        display: none;
      }
    }

    @media (min-width: ${media.medium}) {
      display: flex;
      gap: ${space.xxxSmall};
      margin-top: ${space.small};
    }

    /*generated with Input range slider CSS style generator (version 20211225)
https://toughengineer.github.io/demo/slider-styler*/
    input[type='range'] {
      height: 24px;
      width: 100%;
      -webkit-appearance: none;
    }

    /*progress support*/
    input[type='range'] {
      --range: calc(var(--max) - var(--min));
      --ratio: calc((var(--value) - var(--min)) / var(--range));
      --sx: calc(0.5 * 24px + var(--ratio) * (100% - 24px));
    }

    input[type='range']:focus {
      outline: none;
    }

    /*webkit*/
    input[type='range']::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 24px;
      height: 24px;
      border-radius: 1em;
      background: #80d9ff;
      border: 1px solid #001921;
      box-shadow: none;
      margin-top: calc(
        max((8px - 1px - 1px) * 0.5, 0px) - max(24px * 0.5, 1px)
      );
    }

    input[type='range']::-webkit-slider-runnable-track {
      height: 8px;
      border: 1px solid #001921;
      border-radius: 0.5em;
      background: #fff;
      box-shadow: none;
    }

    input[type='range']::-webkit-slider-runnable-track {
      background: linear-gradient(#000000, #000000) 0 / var(--sx) 100% no-repeat,
        #fff;
    }

    /*mozilla*/
    input[type='range']::-moz-range-thumb {
      width: max(calc(24px - 1px - 1px), 0px);
      height: max(calc(24px - 1px - 1px), 0px);
      border-radius: 1em;
      background: #80d9ff;
      border: 1px solid #001921;
      box-shadow: none;
    }

    input[type='range']::-moz-range-track {
      height: max(calc(8px - 1px - 1px), 0px);
      border: 1px solid #001921;
      border-radius: 0.5em;
      background: #fff;
      box-shadow: none;
    }

    input[type='range']::-moz-range-track {
      background: linear-gradient(#000000, #000000) 0 / var(--sx) 100% no-repeat,
        #fff;
    }

    /*ms*/
    input[type='range']::-ms-fill-upper {
      background: transparent;
      border-color: transparent;
    }

    input[type='range']::-ms-fill-lower {
      background: transparent;
      border-color: transparent;
    }

    input[type='range']::-ms-thumb {
      width: 24px;
      height: 24px;
      border-radius: 1em;
      background: #80d9ff;
      border: 1px solid #001921;
      box-shadow: none;
      margin-top: 0;
      box-sizing: border-box;
    }

    input[type='range']::-ms-track {
      height: 8px;
      border-radius: 0.5em;
      background: #fff;
      border: 1px solid #001921;
      box-shadow: none;
      box-sizing: border-box;
    }

    input[type='range']::-ms-fill-lower {
      height: max(calc(8px - 1px - 1px), 0px);
      border-radius: 0.5em 0 0 0.5em;
      margin: -1px 0 -1px -1px;
      background: #000000;
      border: 1px solid #001921;
      border-right-width: 0;
    }
  `};
`;

// JSX

const CalcInput: FC<InputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  id,
  type,
}) => {
  const rangeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const inputRef = rangeInputRef.current;

    if (inputRef) {
      inputRef.style.setProperty('--value', value.toString());
      inputRef.style.setProperty('--min', min.toString());
      inputRef.style.setProperty('--max', max.toString());

      inputRef.addEventListener('input', () => {
        inputRef.style.setProperty('--value', inputRef.value);
      });
    }

    return () => {
      if (inputRef) {
        inputRef.removeEventListener('input', () => {
          inputRef.style.setProperty('--value', inputRef.value);
        });
      }
    };
  }, [value, min, max]);

  return (
    <StyledCalcInput>
      <StyledInputHeader>
        <Typography component="label" variant="bodyBold" htmlFor={id}>
          {label}
        </Typography>
        <Input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          id={id}
        />
      </StyledInputHeader>
      <StyledMinMax>
        <Typography component="span" variant="bodyBold">
          {type === 'amount' && '£'}
          {min}
          {type === 'interest' && '%'}
        </Typography>
        <Typography component="span" variant="bodyBold">
          {type === 'amount' && '£'}
          {max}
          {type === 'interest' && '%'}
        </Typography>
      </StyledMinMax>
      <StyledRange>
        <Typography component="span" variant="bodyBold">
          {type === 'amount' && '£'}
          {min}
          {type === 'interest' && '%'}
        </Typography>
        <input
          type="range"
          ref={rangeInputRef}
          min={min}
          max={max}
          step="1"
          value={value}
          onChange={onChange}
          id={id}
          tabIndex={-1}
        />
        <Typography component="span" variant="bodyBold">
          {type === 'amount' && '£'}
          {max}
          {type === 'interest' && '%'}
        </Typography>
      </StyledRange>
    </StyledCalcInput>
  );
};

export const Calculator: FC<CalculatorProps> = ({
  type,
  defaultInterestRate,
  defaultAmount,
  defaultNumberOfDays,
  maxAmount,
  maxInterestRate,
  maxNumberOfDays,
  stacked,
}) => {
  const [interestRate, setInterestRate] = useState<number>(defaultInterestRate);
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [numberOfDays, setNumberOfDays] = useState<number>(defaultNumberOfDays);
  const [finalCost, setFinalCost] = useState<number>(0);
  const [dailyInterestRate, setDailyInterestRate] = useState<string>('');
  const [representativeAPR, setRepresentativeAPR] = useState<number>(0);
  const { dataLayerPush } = useDataLayerPush();

  useEffect(() => {
    calculateOverdraft();
  }, [interestRate, amount, numberOfDays]);

  const calculateOverdraft = (): void => {
    const effectiveAmount = amount > 5 ? amount - 5 : 0;
    const dailyInterestRate = ((interestRate / 365 / 100) * 100).toFixed(4); // Round to 4 decimal places
    const formattedDailyInterestRate = dailyInterestRate.padEnd(6, '0'); // Ensure it always has 4 decimal places
    const dailyInterestCost =
      (effectiveAmount * parseFloat(formattedDailyInterestRate)) / 100;
    const interestCostForPeriod = dailyInterestCost * numberOfDays;
    const finalCost =
      interestCostForPeriod >= 1 ? interestCostForPeriod.toFixed(2) : '0.00'; // Round to 2 decimal places
    setFinalCost(parseFloat(finalCost));
    setDailyInterestRate(`${formattedDailyInterestRate}`);

    // Calculate Representative APR
    const monthlyCostOfCredit =
      effectiveAmount * (1 + interestRate / 12 / 100 - 1);
    const interestRateOnTotalBorrowing =
      amount === 0 ? 0 : ((amount + monthlyCostOfCredit) / amount - 1) * 12;
    const representativeAPR =
      ((1 + interestRateOnTotalBorrowing / 12) ** 12 - 1) * 100;
    setRepresentativeAPR(Number(representativeAPR.toFixed(1))); // Convert to number and fix to 1 decimal place
  };

  const handleInterestRateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    let value = Number(event.target.value);
    if (value === 100 && type === 'overdraft') {
      value = 99.9;
    }
    if (value === 50 && type === 'creditCard') {
      value = 49.9;
    }
    setInterestRate(value);

    dataLayerPush({
      event: 'calculator_interaction',
      calculatorType: type,
      interactionType: 'interest_rate_change',
    });
  };

  const handleAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setAmount(Number(event.target.value));

    dataLayerPush({
      event: 'calculator_interaction',
      calculatorType: type,
      interactionType: 'amount_change',
    });
  };

  const handleNumberOfDaysChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNumberOfDays(Number(event.target.value));

    dataLayerPush({
      event: 'calculator_interaction',
      calculatorType: type,
      interactionType: 'number_of_days_change',
    });
  };

  return (
    <StyledContainer stacked={stacked}>
      <StyledForm>
        <Stack gap="fluidSmallToMedium">
          {type === 'overdraft' ? (
            <>
              <CalcInput
                label="Interest rate"
                value={interestRate}
                onChange={handleInterestRateChange}
                min={1}
                id="interestRate"
                max={maxInterestRate}
                type="interest"
              />
              <Divider />
              <CalcInput
                label="Amount"
                value={amount}
                onChange={handleAmountChange}
                min={0}
                max={maxAmount}
                id="amount"
                type="amount"
              />
              <Divider />
              <CalcInput
                label="Number of Days"
                value={numberOfDays}
                onChange={handleNumberOfDaysChange}
                min={1}
                max={maxNumberOfDays}
                id="numberOfDays"
                type="days"
              />
            </>
          ) : (
            <>
              <CalcInput
                label="Amount you borrow"
                value={amount}
                onChange={handleAmountChange}
                min={0}
                max={maxAmount}
                id="amount"
                type="amount"
              />

              <Divider />
              <CalcInput
                label="Interest rate"
                value={interestRate}
                onChange={handleInterestRateChange}
                min={1}
                id="interestRate"
                max={maxInterestRate}
                type="interest"
              />
              <Divider />
              <CalcInput
                label="Number of Days you borrow for"
                value={numberOfDays}
                onChange={handleNumberOfDaysChange}
                min={1}
                max={maxNumberOfDays}
                id="numberOfDays"
                type="days"
              />
            </>
          )}
        </Stack>
      </StyledForm>
      <StyledResults>
        <Stack gap="medium">
          <Stack gap="small">
            <Typography component="h3" variant="headingXSmall">
              Summary of charges
            </Typography>
            <Typography component="p" variant="secondaryHeadingSmall">
              Borrowing{' '}
              <Typography component="span" variant="secondaryHeadingSmallBold">
                £{amount}
              </Typography>{' '}
              for{' '}
              <Typography component="span" variant="secondaryHeadingSmallBold">
                {numberOfDays}
              </Typography>{' '}
              days at{' '}
              <Typography component="span" variant="secondaryHeadingSmallBold">
                {interestRate}%
              </Typography>{' '}
              interest* will cost you
            </Typography>
            <Stack gap="xxxxxSmall">
              <Typography component="p" variant="headingMedium">
                £{finalCost.toFixed(2)}
              </Typography>
              <Typography component="p" variant="bodyLargeBold">
                in interest
              </Typography>
            </Stack>
          </Stack>

          <Stack gap="medium">
            <Divider />
            <Typography component="div" variant="bodySmall">
              <p>*{dailyInterestRate}% - daily interest rate</p>
              <p>
                Representative APR {representativeAPR.toFixed(1)}% (variable)
              </p>
            </Typography>
          </Stack>
        </Stack>
      </StyledResults>
    </StyledContainer>
  );
};

export default Calculator;
